import { Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CreateProcessMapPayload,
  ProcessesMapsInputProps,
  ProcessMapDto,
} from 'types/processesTypes';
import { InputType } from 'types/genericTypes';
import {
  PROCESS_DESCRIPTION_MAX_LENGTH,
  PROCESS_NAME_MAX_LENGTH,
} from 'constants/processMaps';
import { DarwinCol, DarwinRow, DarwinSpin } from 'components/darwin-ui';
import { GenericInput } from '../GenericInput';
import { useGetProductsTree } from 'api/services/productsService';
import { useUI } from 'contexts/UiContext';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { extractQueryData } from 'utils/services';
import { transformToProductTreeData } from '../roles/EditRoles/TreeSection/utils';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ProcessMapEudrRegulationSection } from './ProcessMapEudrRegulationSection';

interface ProcessMapFormMainSectionProps {
  control: Control<CreateProcessMapPayload>;
  errors: FieldErrors<CreateProcessMapPayload>;
  isViewForm?: boolean;
  processMapByIdData?: ProcessMapDto;
  isCreateForm?: boolean;
}

export const ProcessMapFormMainSection = ({
  control,
  errors,
  isViewForm,
  processMapByIdData,
  isCreateForm,
}: ProcessMapFormMainSectionProps) => {
  const { id: processMapId } = useParams();
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const { selectedCompany } = useUI();

  const productTreeData = useGetProductsTree(selectedCompany || '');
  const isError = productTreeData?.isError;
  const isLoading = productTreeData?.isLoading;
  const productsTree = extractQueryData(productTreeData, []);
  const associatedProducts = transformToProductTreeData(productsTree, t);

  useEffect(() => {
    if (isError) {
      openNotification(
        t('error'),
        t('process-map.new-process-map.error-fetching-products'),
        'error'
      );
    }
  }, [isError, openNotification, t]);

  const inputs: ProcessesMapsInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'name',
      label: t('process-map.new-process-map.process-name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('process-map.new-process-map.process-name-required'),
        maxLength: {
          value: PROCESS_NAME_MAX_LENGTH,
          message: t('process-map.new-process-map.max-length-error', {
            fieldName: t('process-map.new-process-map.process-name'),
            maxLength: PROCESS_DESCRIPTION_MAX_LENGTH,
          }),
        },
      },
      maxLength: PROCESS_NAME_MAX_LENGTH,
      disabled: isViewForm,
    },
    {
      inputType: InputType.TEXTAREA,
      fieldName: 'description',
      label: t('process-map.new-process-map.process-description'),
      showRequiredAsterisk: true,
      showCount: !isViewForm,
      inputRules: {
        required: t('process-map.new-process-map.process-description-required'),
        maxLength: {
          value: PROCESS_DESCRIPTION_MAX_LENGTH,
          message: t('process-map.new-process-map.max-length-error', {
            fieldName: t('process-map.new-process-map.process-description'),
            maxLength: PROCESS_DESCRIPTION_MAX_LENGTH,
          }),
        },
      },
      maxLength: PROCESS_DESCRIPTION_MAX_LENGTH,
      disabled: isViewForm,
    },
    {
      inputType: InputType.TREESELECT,
      fieldName: 'finalProducts',
      label: t('process-map.new-process-map.process-products'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('process-map.new-process-map.process-products-required'),
      },
      dropdownTreeData: associatedProducts,
      treeCheckable: true,
      treeNodeFilterProp: 'title',
      disabled: isViewForm,
      isDropdownMultiple: true,
      enableSearchInDropdown: true,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'externalCode',
      label: t('process-flow.data-capture.external-code'),
      inputRules: {
        pattern: {
          value: /^[0-9]+$/,
          message: t('process-flow.data-capture.only-numbers'),
        },
        min: 1,
        max: 999,
      },
      disabled: isViewForm,
    },
  ];

  return (
    <>
      <DarwinSpin fullscreen spinning={isLoading} />
      <DarwinRow gutter={16}>
        <DarwinCol span={12}>
          {inputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
        <ProcessMapEudrRegulationSection
          control={control}
          processMapByIdData={processMapByIdData}
          processMapId={processMapId}
          isCreateForm={isCreateForm}
        />
      </DarwinRow>
    </>
  );
};
